import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2, ShieldCheck, AlertCircle, FileText } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;

export default function FormularioPublico() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios
      .get(`${backend}/api/public/formularios/${token}`)
      .then((r) => {
        if (r.data.estado === "completado") setSubmitted(true);
        if (r.data.respuestas && typeof r.data.respuestas === "object") {
          setAnswers(r.data.respuestas);
        }
        setState({ loading: false, error: null, data: r.data });
      })
      .catch((e) => {
        setState({
          loading: false,
          error: e.response?.data?.detail || "No se pudo cargar el formulario",
          data: null,
        });
      });
  }, [token]);

  const set = (cid, val) => setAnswers((s) => ({ ...s, [cid]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${backend}/api/public/formularios/${token}`, { respuestas: answers });
      setSubmitted(true);
      toast.success("Formulario enviado con éxito");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el formulario");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Frame>
        <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#7A1E2E] mx-auto" /></div>
      </Frame>
    );
  }

  if (state.error) {
    return (
      <Frame>
        <div className="py-16 text-center">
          <AlertCircle className="h-12 w-12 text-[#C25A5A] mx-auto" strokeWidth={1.5} />
          <h2 className="mt-4 font-display text-2xl text-[#1A0810]">Enlace no válido</h2>
          <p className="mt-2 text-sm text-[#6B5D57] max-w-md mx-auto">{state.error}</p>
        </div>
      </Frame>
    );
  }

  if (submitted) {
    return (
      <Frame despacho={state.data?.despacho_nombre}>
        <div className="py-16 text-center" data-testid="formulario-completado">
          <div className="h-16 w-16 rounded-full bg-[#EEF6EE] border border-[#1F5B2E]/25 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-[#1F5B2E]" strokeWidth={1.6} />
          </div>
          <h2 className="mt-6 font-display text-3xl text-[#1A0810]">¡Formulario enviado!</h2>
          <p className="mt-3 text-sm text-[#6B5D57] max-w-md mx-auto leading-relaxed">
            Gracias por completar la información. El despacho ha recibido sus respuestas y se pondrá en contacto para los siguientes pasos.
          </p>
        </div>
      </Frame>
    );
  }

  const data = state.data;
  return (
    <Frame despacho={data.despacho_nombre}>
      <div className="py-8" data-testid="formulario-publico">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Formulario</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#1A0810]">{data.titulo}</h1>
        {data.descripcion && <p className="mt-3 text-sm text-[#4a3838] max-w-2xl leading-relaxed">{data.descripcion}</p>}
        {data.cliente_nombre && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F5EEE0] border border-[#E5DFD3] px-3 py-1 text-[12px] text-[#4a3838]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#7A1E2E]" strokeWidth={2} />
            Enviado a: <strong>{data.cliente_nombre}</strong>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 space-y-8">
          {(data.secciones || []).map((sec, sIdx) => (
            <section key={sec.id || sIdx} className="space-y-5">
              {(data.secciones.length > 1 || sec.titulo) && (
                <div className="border-l-4 border-[#7A1E2E] pl-4 py-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A1E2E] font-semibold">
                    Sección {sIdx + 1} de {data.secciones.length}
                  </div>
                  <h2 className="font-display text-xl text-[#1A0810] mt-0.5">{sec.titulo || "General"}</h2>
                  {sec.descripcion && <p className="text-sm text-[#6B5D57] mt-1">{sec.descripcion}</p>}
                </div>
              )}
              <div className="space-y-5">
                {(sec.campos || []).map((campo) => (
                  <CampoRenderer key={campo.id} campo={campo} value={answers[campo.id]} onChange={(v) => set(campo.id, v)} />
                ))}
              </div>
            </section>
          ))}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="formulario-submit"
              className="w-full sm:w-auto h-12 px-8 bg-[#1F5B2E] hover:bg-[#144320] text-white"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar formulario"}
            </Button>
            <p className="mt-3 text-[11px] text-[#6B5D57]">
              Al enviar, la información será compartida únicamente con {data.despacho_nombre}. Nunca solicitamos contraseñas ni datos bancarios por este medio.
            </p>
          </div>
        </form>
      </div>
    </Frame>
  );
}

function Frame({ children, despacho }) {
  return (
    <div className="min-h-screen bg-[#F5EEE0]">
      <header className="bg-[#2B0913] text-[#F1EDE4] py-5 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="/vertice-logo.png" alt="Vértice" className="h-9 w-9" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C25A5A] font-semibold">Grupo Vértice CR</div>
            <div className="text-base">{despacho || "Despacho Legal"}</div>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6">
        <div className="my-8 rounded-xl bg-white border border-[#E5DFD3] p-6 sm:p-10">
          {children}
        </div>
        <p className="pb-8 text-center text-[11px] text-[#6B5D57]">
          © {new Date().getFullYear()} Grupo Vértice CR · Despacho Legal
        </p>
      </main>
    </div>
  );
}

function CampoRenderer({ campo, value, onChange }) {
  const label = (
    <Label className="text-[11px] uppercase tracking-[0.18em] text-[#4a3838] font-semibold">
      {campo.label}{campo.required && <span className="text-[#C25A5A]"> *</span>}
    </Label>
  );
  const help = campo.help_text ? (
    <p className="mt-1 text-[11px] text-[#6B5D57]">{campo.help_text}</p>
  ) : null;
  const inputCls = "mt-2 h-11 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]";
  const testid = `campo-${campo.id}`;

  switch (campo.tipo) {
    case "textarea":
      return (
        <div>
          {label}
          <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder} className="mt-2 min-h-[100px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]" />
          {help}
        </div>
      );
    case "number":
      return (
        <div>{label}<Input data-testid={testid} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder={campo.placeholder} className={inputCls} />{help}</div>
      );
    case "date":
      return (
        <div>{label}<Input data-testid={testid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />{help}</div>
      );
    case "email":
      return (
        <div>{label}<Input data-testid={testid} type="email" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={campo.placeholder || "correo@ejemplo.com"} className={inputCls} />{help}</div>
      );
    case "tel":
      return (
        <div>{label}<Input data-testid={testid} type="tel" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={campo.placeholder || "+506 8888 8888"} className={inputCls} />{help}</div>
      );
    case "cedula":
      return (
        <div>{label}<Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={campo.placeholder || "1-2345-6789"} className={inputCls} />{help}
          <p className="mt-1 text-[11px] text-[#6B5D57]">Física: 9 dígitos · Jurídica: 10 dígitos iniciando en 3</p>
        </div>
      );
    case "select":
      return (
        <div>{label}
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid={testid} className={inputCls}><SelectValue placeholder="Seleccione una opción" /></SelectTrigger>
            <SelectContent>{(campo.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          {help}
        </div>
      );
    case "multiselect": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
      return (
        <div>{label}
          <div className="mt-2 space-y-2" data-testid={testid}>
            {(campo.options || []).map((o) => (
              <label key={o} className="flex items-center gap-3 rounded-md border border-[#E5DFD3] bg-white px-3 py-2 cursor-pointer hover:border-[#7A1E2E]/40">
                <Checkbox checked={arr.includes(o)} onCheckedChange={() => toggle(o)} data-testid={`${testid}-${o}`} />
                <span className="text-sm text-[#1A0810]">{o}</span>
              </label>
            ))}
          </div>
          {help}
        </div>
      );
    }
    case "boolean":
      return (
        <div className="flex items-start gap-3 rounded-md border border-[#E5DFD3] bg-[#FBF9F4] p-3">
          <Switch data-testid={testid} checked={!!value} onCheckedChange={onChange} className="mt-0.5" />
          <div className="flex-1">
            <span className="text-sm text-[#1A0810] font-medium">{campo.label}</span>
            {campo.help_text && <p className="mt-0.5 text-[11px] text-[#6B5D57]">{campo.help_text}</p>}
          </div>
        </div>
      );
    default:
      return (
        <div>{label}<Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={campo.placeholder} className={inputCls} />{help}</div>
      );
  }
}
